import { useMemo, type CSSProperties } from 'react';

type MyNazcaRangeFieldProps = {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  disabled?: boolean;
  hint?: string;
  formatValue?: (value: number) => string;
  onChange: (value: number) => void;
  onPointerDown?: () => void;
  onPointerUp?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyUp?: () => void;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export function MyNazcaRangeField({
  label,
  min,
  max,
  step,
  value,
  disabled,
  hint,
  formatValue,
  onChange,
  onPointerDown,
  onPointerUp,
  onFocus,
  onBlur,
  onKeyUp,
}: MyNazcaRangeFieldProps) {
  const safeValue = clamp(value, min, max);
  const displayValue = formatValue ? formatValue(safeValue) : String(safeValue);
  const thumbLeftPct = useMemo(() => {
    if (max <= min) return 0;
    return ((safeValue - min) / (max - min)) * 100;
  }, [safeValue, min, max]);

  return (
    <label className="mynazca-app__field mynazca-app__field--range">
      <span className="mynazca-app__field-label">{label}</span>
      <div
        className="mynazca-app__range-wrap"
        style={{
          '--range-fill-pct': `${thumbLeftPct}%`,
          '--range-thumb-pct': thumbLeftPct,
        } as CSSProperties}
      >
        <div className="mynazca-app__range-track" aria-hidden />
        <input
          type="range"
          className="mynazca-app__range-input"
          min={min}
          max={max}
          step={step}
          value={safeValue}
          disabled={disabled}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onKeyUp}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span className="mynazca-app__range-thumb-val" aria-hidden>
          {displayValue}
        </span>
      </div>
      {hint != null && hint !== '' && (
        <p className="mynazca-app__field-hint">{hint}</p>
      )}
    </label>
  );
}
